import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { trendingPrompts, promptCategories, type TrendingPrompt } from '@/data/trendingPrompts';
import { toast } from 'sonner';

interface TrendingPromptSuggestionsProps {
  onSelectPrompt: (prompt: TrendingPrompt) => void;
}

const TrendingPromptSuggestions: React.FC<TrendingPromptSuggestionsProps> = ({ onSelectPrompt }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filteredPrompts = selectedCategory === 'All' 
    ? trendingPrompts 
    : trendingPrompts.filter(p => p.category === selectedCategory);

  const handleCopyPrompt = async (prompt: TrendingPrompt) => {
    try {
      await navigator.clipboard.writeText(prompt.prompt);
      setCopiedId(prompt.id);
      toast.success('Prompt copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy prompt');
    }
  };

  const getTagColor = (tag: string) => {
    switch (tag) {
      case 'trending':
        return 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-300 border-pink-500/30';
      case 'popular':
        return 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border-blue-500/30';
      case 'new':
        return 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border-green-500/30';
      default:
        return 'bg-white/10 text-gray-300 border-white/20';
    }
  };

  return (
    <Card className="bg-black/20 backdrop-blur-xl border-white/10 text-white overflow-hidden">
      <CardHeader className="cursor-pointer hover:bg-white/5 transition-colors" onClick={() => setIsExpanded(!isExpanded)}>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span>Trending Prompts</span>
            <Badge className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-300 border-pink-500/30">
              {filteredPrompts.length} templates
            </Badge>
          </div>
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {promptCategories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category 
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0" 
                  : "bg-white/5 border-white/20 text-white hover:bg-white/10"
                }
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Prompts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPrompts.map((promptData) => (
              <Card 
                key={promptData.id} 
                className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 hover:border-purple-500/30 transition-all duration-300 group"
              >
                <CardContent className="p-4 space-y-3">
                  {/* Header with tags */}
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-white text-sm leading-tight flex-1">
                      {promptData.title}
                    </h3>
                    <div className="flex gap-1">
                      {promptData.tags.map((tag) => (
                        <Badge 
                          key={tag} 
                          className={`text-xs px-1.5 py-0.5 ${getTagColor(tag)}`}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Prompt Preview */}
                  <p className="text-xs text-gray-300 line-clamp-3 leading-relaxed">
                    {promptData.prompt}
                  </p>

                  {/* Metadata */}
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Badge variant="outline" className="bg-white/5 border-white/20 text-gray-300">
                      {promptData.suggestedSize}
                    </Badge>
                    <Badge variant="outline" className="bg-white/5 border-white/20 text-gray-300">
                      {promptData.estimatedQuality}
                    </Badge>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => onSelectPrompt(promptData)}
                      size="sm"
                      className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 text-xs"
                    >
                      Use This Prompt
                    </Button>
                    <Button
                      onClick={() => handleCopyPrompt(promptData)}
                      size="sm"
                      variant="outline"
                      className="bg-white/5 border-white/20 text-white hover:bg-white/10"
                    >
                      {copiedId === promptData.id ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredPrompts.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <p>No prompts found in this category</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default TrendingPromptSuggestions;
